(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.FFCProductFinderUI = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const VERSION = 'native-v12-measurement';
  const STORAGE_PREFIX = 'ffc-product-finder-wizard:';
  const KLAVIYO_REVISION = '2026-07-15';
  const KLAVIYO_METRIC_NAME = 'FFC Product Finder Results Requested';
  const RESULTS_CONTRACT_VERSION = 'ffc-native-results-v1';
  const HANDLE_TO_ROUTE = Object.freeze({
    '2d-sonar-mastery-video-course': 'course-2d',
    'down-imaging-mastery-video-course': 'course-down',
    'forward-facing-sonar-bundle': 'bundle-ffs',
    'forward-facing-sonar-course': 'course-ffs',
    'full-boat-bundle-ffs-and-3-in-1-sonar-courses-settings-guides': 'bundle-full-boat',
    'humminbird-360-mega-360-settings-guide': 'guide-mega-360',
    'side-imaging-mastery-video-course': 'course-side',
    'sonar-mastery-complete-bundle': 'bundle-3-in-1',
    'sonar-mastery-video-course-bundle': 'course-bundle-3-in-1',
  });

  function publicKlaviyoUrl(path, companyId) {
    const id = String(companyId || '').trim();
    if (!/^[A-Za-z0-9]+$/.test(id)) throw new Error('The Klaviyo public site ID is not configured.');
    return `https://a.klaviyo.com${path}?company_id=${encodeURIComponent(id)}`;
  }

  function buildResultsEventProperties(config) {
    const selection = config.selection || {};
    const recommendations = Array.isArray(config.recommendations)
      ? config.recommendations
      : config.recommendation ? [config.recommendation] : [];
    if (recommendations.length < 1 || recommendations.length > 2) {
      throw new Error('The recommendation route is not supported for email.');
    }
    const snapshot = recommendations.map(function (recommendation) {
      const routeCode = HANDLE_TO_ROUTE[recommendation && recommendation.handle];
      const variantId = Number(recommendation && recommendation.variantId);
      if (!routeCode || !Number.isSafeInteger(variantId) || variantId <= 0) {
        throw new Error('The recommendation route is not supported for email.');
      }
      return { routeCode, variantId: String(variantId) };
    });
    const sonarCodes = Array.isArray(selection.sonarModes) ? selection.sonarModes.slice() : [];
    if (selection.hasFfs && !sonarCodes.includes('ffs')) sonarCodes.push('ffs');
    if (selection.mega360 && !sonarCodes.includes('mega360')) sonarCodes.push('mega360');
    const hardwareCodes = [
      `conventional_brand:${selection.conventionalBrand || 'none'}`,
      `conventional:${selection.conventional || 'none'}`,
      `humminbird_unit:${selection.humminbirdUnit || 'none'}`,
      `ffs_brand:${selection.ffsBrand || 'none'}`,
      `ffs:${selection.ffs || 'none'}`,
      `has_ffs:${Boolean(selection.hasFfs)}`,
      `mega360:${Boolean(selection.mega360)}`,
    ];
    const properties = {
      ffc_product_finder_contract_version: RESULTS_CONTRACT_VERSION,
      ffc_product_finder_request_id: String(config.requestId),
      ffc_product_finder_quiz_version: String(config.quizVersion || VERSION),
      ffc_product_finder_recommendation_count: snapshot.length,
      ffc_product_finder_route_code: snapshot[0].routeCode,
      ffc_product_finder_variant_code: snapshot[0].variantId,
      ffc_product_finder_sonar_codes: sonarCodes,
      ffc_product_finder_hardware_codes: hardwareCodes,
    };
    if (snapshot.length === 2) {
      properties.ffc_product_finder_route_code_2 = snapshot[1].routeCode;
      properties.ffc_product_finder_variant_code_2 = snapshot[1].variantId;
    }
    return properties;
  }

  function buildResultsEmailRecommendations(cardsRoot) {
    if (!cardsRoot || typeof cardsRoot.querySelectorAll !== 'function') return null;
    const productCards = Array.from(cardsRoot.querySelectorAll('article[data-product-handle]'));
    if (productCards.length < 1 || productCards.length > 2) return null;
    const recommendations = [];
    for (const card of productCards) {
      const productLink = card.querySelector('[data-ffc-pdp]');
      const variantId = Number(productLink && productLink.dataset.variantId);
      if (!card.dataset.productHandle || !Number.isSafeInteger(variantId) || variantId <= 0) return null;
      recommendations.push({ handle: card.dataset.productHandle, variantId });
    }
    return recommendations;
  }

  function buildKlaviyoSubscriptionRequest(config) {
    const listId = String(config.listId || '').trim();
    if (!/^[A-Za-z0-9]+$/.test(listId)) throw new Error('The Klaviyo list ID is not configured.');
    return {
      url: publicKlaviyoUrl('/client/subscriptions', config.companyId),
      options: {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'FFC Product Finder results form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: String(config.email || '').trim(),
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      },
    };
  }

  function buildKlaviyoEventRequest(config) {
    return {
      url: publicKlaviyoUrl('/client/events', config.companyId),
      options: {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: buildResultsEventProperties(config),
              unique_id: String(config.requestId),
              metric: { data: { type: 'metric', attributes: { name: KLAVIYO_METRIC_NAME } } },
              profile: { data: { type: 'profile', attributes: { email: String(config.email || '').trim() } } },
            },
          },
        }),
      },
    };
  }

  async function submitResultsSignup(fetchImpl, config) {
    const subscription = buildKlaviyoSubscriptionRequest(config);
    const subscriptionResponse = await fetchImpl(subscription.url, subscription.options);
    if (!subscriptionResponse || subscriptionResponse.status !== 202) {
      throw new Error('The subscription request was not accepted.');
    }
    const event = buildKlaviyoEventRequest(config);
    const eventResponse = await fetchImpl(event.url, event.options);
    if (!eventResponse || eventResponse.status !== 202) {
      throw new Error('The results event was not accepted.');
    }
    return { accepted: true, status: eventResponse.status, state: 'event_accepted', requestId: config.requestId };
  }

  function createSignupSubmitter(fetchImpl) {
    let inFlight = null;
    return function submitOnce(config) {
      if (inFlight) return inFlight;
      inFlight = submitResultsSignup(fetchImpl, config).finally(function () {
        inFlight = null;
      });
      return inFlight;
    };
  }

  function createRequestId() {
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, function (value) { return value.toString(16).padStart(2, '0'); }).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function isIntentionalClick(event) {
    try {
      const measurement = globalThis.FFCProductFinderMeasurement;
      if (measurement && typeof measurement.isIntentionalClick === 'function') {
        return measurement.isIntentionalClick(event);
      }
    } catch (_) {}
    return Boolean(event && event.isTrusted === true);
  }

  function init(root) {
    if (!root || root.dataset.finderInitialized === 'true') return;
    const finder = globalThis.FFCProductFinder;
    if (!finder) throw new Error('FFCProductFinder core is not loaded.');

    const form = root.querySelector('[data-ffc-finder-form]');
    const wizard = root.querySelector('[data-ffc-wizard]');
    const allSteps = Array.from(root.querySelectorAll('[data-ffc-step]'));
    const backButton = root.querySelector('[data-ffc-back]');
    const nextButton = root.querySelector('[data-ffc-next]');
    const progressLabel = root.querySelector('[data-ffc-progress-label]');
    const progressBar = root.querySelector('[data-ffc-progress-bar]');
    const error = root.querySelector('[data-ffc-step-error]');
    const status = root.querySelector('[data-ffc-finder-status]');
    const results = root.querySelector('[data-ffc-finder-results]');
    const resultsIntro = root.querySelector('[data-ffc-results-intro]');
    const cards = root.querySelector('[data-ffc-result-cards]');
    const answerHardware = root.querySelector('[data-ffc-answer-hardware]');
    const answerSonar = root.querySelector('[data-ffc-answer-sonar]');
    const resultsEmail = root.querySelector('[data-ffc-results-email]');
    const resultsEmailForm = root.querySelector('[data-ffc-results-email-form]');
    const resultsEmailInput = root.querySelector('input[name="ffc_results_email"]');
    const resultsEmailFeedback = root.querySelector('[data-ffc-results-email-feedback]');
    const submitResultsSignupOnce = createSignupSubmitter(fetch);
    const identification = root.querySelector('[data-ffc-identification]');
    const omitted = root.querySelector('[data-ffc-omitted]');
    const restartButton = root.querySelector('[data-ffc-restart]');
    const sectionId = root.dataset.sectionId || 'default';
    const storageKey = STORAGE_PREFIX + sectionId;
    let currentIndex = 0;
    let pendingResultsEmailRequest = null;

    if (!form || !wizard || !allSteps.length || !backButton || !nextButton) return;
    root.dataset.finderInitialized = 'true';

    function analyticsEvent(name, detail) {
      const payload = Object.assign({ event: name, quiz_version: VERSION }, detail || {});
      root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
    }

    function directCartProperties(handle, variantId) {
      try {
        const bridge = globalThis.__ffcProductFinderMeasurementInstallation;
        if (bridge && typeof bridge.directCartProperties === 'function') {
          return bridge.directCartProperties(root, handle, variantId);
        }
      } catch (_) {}
      return {
        _ffc_source: 'product_finder',
        _ffc_finder_version: VERSION,
        _ffc_recommendation: handle,
      };
    }

    function checkedValue(name) {
      const input = form.querySelector(`input[name="${name}"]:checked`);
      return input ? input.value : '';
    }

    function checkedValues(name) {
      return Array.from(form.querySelectorAll(`input[name="${name}"]:checked`)).map(function (input) {
        return input.value;
      });
    }

    function hasSonarMode(value) {
      return checkedValues('sonar_modes').includes(value);
    }

    function hasConventionalImaging() {
      return hasSonarMode('down') || hasSonarMode('side');
    }

    function needsHumminbirdUnit() {
      return hasSonarMode('side') &&
        checkedValue('conventional_brand') === 'humminbird' &&
        checkedValue('conventional') === 'UNKNOWN';
    }

    function shouldShowStep(step) {
      const key = step.dataset.stepKey;
      if (key === 'conventional-brand') return hasConventionalImaging();
      if (key === 'conventional-model') {
        const brand = checkedValue('conventional_brand');
        return hasConventionalImaging() && brand !== 'other' && brand !== 'airmar';
      }
      if (key === 'humminbird-unit') return needsHumminbirdUnit();
      if (key === 'ffs-brand' || key === 'ffs-model') return hasSonarMode('ffs');
      return true;
    }

    function activeSteps() {
      return allSteps.filter(shouldShowStep);
    }

    function syncBrandPanels(name) {
      const value = checkedValue(`${name}_brand`);
      root.querySelectorAll(`[data-${name}-brand-panel]`).forEach(function (panel) {
        panel.hidden = panel.dataset[`${name}BrandPanel`] !== value;
      });
    }

    function clearAnswers(name) {
      form.querySelectorAll(`[name="${name}"]`).forEach(function (input) {
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
        else input.value = '';
      });
    }

    function clearIncompatibleModel(name) {
      const selected = form.querySelector(`input[name="${name}"]:checked`);
      if (!selected || selected.value === 'UNKNOWN') return;
      const brand = checkedValue(`${name}_brand`);
      const panel = selected.closest(`[data-${name}-brand-panel]`);
      if (!brand || !panel || panel.dataset[`${name}BrandPanel`] !== brand) selected.checked = false;
    }

    function sanitizeSonarState() {
      if (!hasConventionalImaging()) {
        clearAnswers('conventional_brand');
        clearAnswers('conventional');
        syncBrandPanels('conventional');
      } else if (['other', 'airmar'].includes(checkedValue('conventional_brand'))) {
        clearAnswers('conventional');
      } else {
        clearIncompatibleModel('conventional');
      }
      if (!needsHumminbirdUnit()) clearAnswers('humminbird_unit');
      if (!hasSonarMode('ffs')) {
        clearAnswers('ffs_brand');
        clearAnswers('ffs');
        syncBrandPanels('ffs');
      } else {
        clearIncompatibleModel('ffs');
      }
    }

    function saveState() {
      const state = { stepKey: activeSteps()[currentIndex] ? activeSteps()[currentIndex].dataset.stepKey : 'sonar-modes', answers: {} };
      new FormData(form).forEach(function (value, key) {
        if (Object.prototype.hasOwnProperty.call(state.answers, key)) {
          state.answers[key] = Array.isArray(state.answers[key])
            ? state.answers[key].concat(value)
            : [state.answers[key], value];
        } else {
          state.answers[key] = value;
        }
      });
      try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) { /* Storage is optional. */ }
    }

    function restoreState() {
      let state;
      try { state = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch (_) { return; }
      if (!state || !state.answers) return;
      if (state.answers.mega360 === 'yes') {
        const savedModes = Array.isArray(state.answers.sonar_modes)
          ? state.answers.sonar_modes.slice()
          : state.answers.sonar_modes ? [state.answers.sonar_modes] : [];
        if (!savedModes.includes('mega360')) savedModes.push('mega360');
        state.answers.sonar_modes = savedModes;
      }
      delete state.answers.mega360;
      Object.keys(state.answers).forEach(function (name) {
        const wanted = Array.isArray(state.answers[name]) ? state.answers[name] : [state.answers[name]];
        form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach(function (input) {
          if (input.type === 'checkbox' || input.type === 'radio') input.checked = wanted.includes(input.value);
          else input.value = wanted[0] || '';
        });
      });
      sanitizeSonarState();
      syncBrandPanels('conventional');
      syncBrandPanels('ffs');
      const steps = activeSteps();
      const restoredIndex = steps.findIndex(function (step) { return step.dataset.stepKey === state.stepKey; });
      if (restoredIndex >= 0) currentIndex = restoredIndex;
    }

    function setStep(index, options) {
      const steps = activeSteps();
      currentIndex = Math.max(0, Math.min(index, steps.length - 1));
      allSteps.forEach(function (step) {
        const active = step === steps[currentIndex];
        step.hidden = !active;
        step.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      const position = currentIndex + 1;
      const percent = Math.round((position / steps.length) * 100);
      progressLabel.textContent = `Question ${position} of ${steps.length}`;
      progressBar.style.width = `${percent}%`;
      progressBar.parentElement.setAttribute('aria-valuenow', String(percent));
      backButton.hidden = currentIndex === 0;
      nextButton.textContent = currentIndex === steps.length - 1 ? 'See my recommendation' : 'Continue';
      error.textContent = '';
      if (!options || options.focus !== false) {
        const legend = steps[currentIndex].querySelector('legend');
        if (legend) {
          legend.focus({ preventScroll: true });
          legend.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }
      saveState();
      analyticsEvent('ffc_quiz_step_view', { step_key: steps[currentIndex].dataset.stepKey, step_number: position, step_count: steps.length });
    }

    function validateStep(step) {
      sanitizeSonarState();
      const key = step.dataset.stepKey;
      let valid = true;
      if (key === 'sonar-modes') valid = checkedValues('sonar_modes').length > 0;
      else {
        const requiredName = step.dataset.requiredName;
        if (requiredName) valid = Boolean(checkedValue(requiredName));
      }
      if (valid) {
        step.removeAttribute('data-invalid');
        error.textContent = '';
        return true;
      }
      step.setAttribute('data-invalid', 'true');
      error.textContent = step.dataset.errorMessage || 'Choose an answer to continue.';
      const firstInput = step.querySelector('input:not([disabled]), select:not([disabled])');
      if (firstInput) firstInput.focus();
      analyticsEvent('ffc_quiz_validation_error', { step_key: key });
      return false;
    }

    function create(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }

    function formatPrice(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(cents || 0) / 100);
    }

    function lookupFailureClass(reason) {
      const message = String(reason && reason.message ? reason.message : reason || '');
      if (message.startsWith('No exact available variant')) return 'exact_variant_unavailable';
      if (message.startsWith('Product lookup failed')) return 'product_lookup_failed';
      return 'unexpected_lookup_error';
    }

    function resultScrollBehavior() {
      return globalThis.matchMedia && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    function appendIncluded(container, resolved, selectedLaminatedValue) {
      const included = finder.describeIncluded(resolved);
      const box = create('details', 'ffc-product-finder__included');
      box.open = Boolean(globalThis.matchMedia && globalThis.matchMedia('(min-width: 821px)').matches);
      box.appendChild(create('summary', '', 'What’s included'));
      const content = create('div', 'ffc-product-finder__included-content');
      const list = create('ul');
      included.settingsGuides.concat(included.videoCourses).forEach(function (item) {
        list.appendChild(create('li', '', item));
      });
      content.appendChild(list);
      included.notes.forEach(function (note) {
        content.appendChild(create('p', 'ffc-product-finder__included-note', note));
      });
      let laminatedNote = null;
      if (resolved.hasLaminatedOption) {
        laminatedNote = create('p', 'ffc-product-finder__included-note', `Mailed laminated quick settings sheet: ${selectedLaminatedValue}.`);
        laminatedNote.dataset.ffcLaminatedIncluded = '';
        content.appendChild(laminatedNote);
      }
      box.appendChild(content);
      container.appendChild(box);
      return laminatedNote;
    }

    function humminbirdCableConfirmation(selection) {
      if (selection.humminbirdUnit === 'xplore') return 'Confirm the transducer cable says CMSI.';
      if (selection.humminbirdUnit === 'helix-solix-apex') return 'Confirm the transducer cable says MSI or MSI+.';
      return '';
    }

    function customerOptionValue(value) {
      const labels = {
        'Humminbird HELIX/SOLIX/APEX: MEGA SI': 'Humminbird MEGA Imaging',
        'Humminbird XPLORE: Compact MEGA': 'Humminbird Compact MEGA Imaging',
      };
      return labels[value] || value;
    }

    function renderCard(resolved, index, cableConfirmation) {
      const article = create('article', 'ffc-product-finder__card');
      article.dataset.productHandle = resolved.request.handle;
      let image = null;
      if (resolved.imageUrl) {
        image = create('img', 'ffc-product-finder__product-image');
        image.src = resolved.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        article.appendChild(image);
      }
      const body = create('div', 'ffc-product-finder__card-body');
      body.appendChild(create('p', 'ffc-product-finder__card-label', index === 0 ? 'Best match' : 'Optional add-on'));
      body.appendChild(create('h2', '', resolved.title));

      const laminatedOption = resolved.selectedOptions.find(function (option) {
        return /laminated|quick reference/i.test(option.name);
      });
      const laminatedChoices = Array.isArray(resolved.laminatedChoices) ? resolved.laminatedChoices : [];
      const hasInteractiveLaminatedChoice = laminatedOption && laminatedChoices.length > 1;
      const options = create('dl', 'ffc-product-finder__options');
      resolved.selectedOptions.forEach(function (option) {
        if (hasInteractiveLaminatedChoice && option === laminatedOption) return;
        options.appendChild(create('dt', '', option.name));
        options.appendChild(create('dd', '', customerOptionValue(option.value)));
      });
      if (options.children.length) body.appendChild(options);
      if (cableConfirmation) {
        body.appendChild(create('p', 'ffc-product-finder__confirmation', cableConfirmation));
      }

      const laminatedInputs = [];
      if (hasInteractiveLaminatedChoice) {
        const selector = create('fieldset', 'ffc-product-finder__laminated-choice');
        selector.dataset.ffcLaminatedChoice = '';
        selector.appendChild(create('legend', '', 'Do you want a single page laminated quick settings sheet to be mailed to you as well?'));
        const choices = create('div', 'ffc-product-finder__laminated-options');
        laminatedChoices.forEach(function (choice) {
          const label = create('label', 'ffc-product-finder__laminated-option');
          const input = create('input');
          input.type = 'radio';
          input.name = `ffc_laminated_${index}`;
          input.value = choice.value;
          input.checked = choice.variantId === resolved.variantId;
          label.appendChild(input);
          label.appendChild(create('span', '', choice.value));
          choices.appendChild(label);
          laminatedInputs.push({ input, choice });
        });
        selector.appendChild(choices);
        selector.appendChild(create('p', 'ffc-product-finder__laminated-help', 'Digital + Laminated Sheet includes the single-page laminated quick settings sheet mailed to you. Digital Only keeps the digital product without the mailed sheet. The price and exact cart item update with your choice.'));
        body.appendChild(selector);
      }

      const price = create('p', 'ffc-product-finder__price', formatPrice(resolved.priceCents));
      body.appendChild(price);
      const currentVariant = {
        variantId: resolved.variantId,
        priceCents: resolved.priceCents,
        imageUrl: resolved.imageUrl,
        url: resolved.url,
      };

      const actions = create('form', 'ffc-product-finder__actions');
      actions.addEventListener('submit', function (event) { event.preventDefault(); });
      const variantInput = create('input');
      variantInput.type = 'hidden';
      variantInput.name = 'id';
      variantInput.value = String(currentVariant.variantId);
      const add = create('button', 'ffc-product-finder__add', 'Add to cart');
      add.type = 'button';
      add.dataset.ffcAddToCart = '';
      add.dataset.variantId = String(currentVariant.variantId);
      const feedback = create('p', 'ffc-product-finder__cart-feedback');
      feedback.setAttribute('aria-live', 'polite');
      const link = create('a', 'ffc-product-finder__details-link', 'View full product details');
      link.href = currentVariant.url;
      link.dataset.ffcPdp = '';
      link.dataset.variantId = String(currentVariant.variantId);
      link.dataset.recommendationPosition = String(index + 1);
      actions.appendChild(variantInput);
      actions.appendChild(add);
      actions.appendChild(link);
      actions.appendChild(feedback);
      body.appendChild(actions);
      if (resolved.request.noSettingsGuideMessage) {
        body.appendChild(create('p', 'ffc-product-finder__guide-notice', resolved.request.noSettingsGuideMessage));
      }
      body.appendChild(create('h3', 'ffc-product-finder__reason-heading', 'Why this fits'));
      body.appendChild(create('p', 'ffc-product-finder__reason', resolved.request.reason));
      const laminatedNote = appendIncluded(body, resolved, laminatedOption ? laminatedOption.value : 'Digital Only');
      article.appendChild(body);

      laminatedInputs.forEach(function (entry) {
        entry.input.addEventListener('change', function () {
          if (!entry.input.checked) return;
          currentVariant.variantId = entry.choice.variantId;
          currentVariant.priceCents = entry.choice.priceCents;
          currentVariant.imageUrl = entry.choice.imageUrl;
          currentVariant.url = entry.choice.url;
          variantInput.value = String(currentVariant.variantId);
          add.dataset.variantId = String(currentVariant.variantId);
          add.disabled = false;
          add.textContent = 'Add to cart';
          feedback.textContent = '';
          price.textContent = formatPrice(currentVariant.priceCents);
          link.href = currentVariant.url;
          link.dataset.variantId = String(currentVariant.variantId);
          if (image && currentVariant.imageUrl) image.src = currentVariant.imageUrl;
          if (laminatedNote) laminatedNote.textContent = `Mailed laminated quick settings sheet: ${entry.choice.value}.`;
          analyticsEvent('ffc_quiz_laminated_option_changed', {
            product_handle: resolved.request.handle,
            variant_id: currentVariant.variantId,
            laminated_card: entry.choice.value,
          });
        });
      });

      add.addEventListener('click', async function () {
        add.disabled = true;
        add.textContent = 'Adding…';
        feedback.textContent = '';
        const properties = directCartProperties(resolved.request.handle, currentVariant.variantId);
        analyticsEvent('ffc_quiz_atc_attempt', {
          product_handle: resolved.request.handle,
          variant_id: currentVariant.variantId,
          attribution_route: properties._ffc_attribution_route || 'direct_cart',
          attribution_token: properties._ffc_attribution_token || '',
        });
        try {
          const addedProduct = await finder.addVariantToCart(currentVariant.variantId, fetch, location.origin, properties);
          add.textContent = 'Added to cart';
          feedback.textContent = 'The exact selected variant is in your cart.';
          actions.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { product: addedProduct, addToCartBtn: add },
          }));
        } catch (cartError) {
          add.disabled = false;
          add.textContent = 'Add to cart';
          feedback.textContent = cartError.message || 'This could not be added. Open the product details instead.';
          analyticsEvent('ffc_quiz_add_to_cart_error', {
            product_handle: resolved.request.handle,
            variant_id: currentVariant.variantId,
            failure_reason_class: 'cart_request_failed',
          });
        }
      });
      link.addEventListener('click', function (event) {
        if (!isIntentionalClick(event)) return;
        analyticsEvent('ffc_quiz_pdp_click', {
          product_handle: resolved.request.handle,
          variant_id: currentVariant.variantId,
          recommendation_position: index + 1,
        });
      });
      return article;
    }

    function confirmedConventionalSelection(hasConventional, hasTwoD) {
      if (!hasConventional) return hasTwoD ? '2D_ONLY' : '';
      const brand = checkedValue('conventional_brand');
      if (['other', 'airmar'].includes(brand)) return 'UNKNOWN';
      const selected = checkedValue('conventional');
      if (brand !== 'humminbird' || selected !== 'UNKNOWN') return selected;
      const unit = checkedValue('humminbird_unit');
      if (unit === 'xplore') return 'Humminbird XPLORE: Compact MEGA';
      if (unit === 'helix-solix-apex') return 'Humminbird HELIX/SOLIX/APEX: MEGA SI';
      return 'UNKNOWN';
    }

    function buildSelection() {
      const sonarModes = checkedValues('sonar_modes');
      const hasConventional = sonarModes.includes('down') || sonarModes.includes('side');
      const hasTwoD = sonarModes.includes('2d');
      const hasFfs = sonarModes.includes('ffs');
      return {
        conventional: confirmedConventionalSelection(hasConventional, hasTwoD),
        conventionalBrand: hasConventional ? checkedValue('conventional_brand') : '',
        humminbirdUnit: needsHumminbirdUnit() ? checkedValue('humminbird_unit') : '',
        ffs: hasFfs ? checkedValue('ffs') : '',
        ffsBrand: hasFfs ? checkedValue('ffs_brand') : '',
        hasFfs,
        mega360: sonarModes.includes('mega360'),
        sonarModes: sonarModes.filter(function (mode) { return mode === '2d' || mode === 'down' || mode === 'side'; }),
        context: {
          frustration: checkedValue('frustration'),
          confidence: checkedValue('confidence'),
          adjustFrequency: checkedValue('adjust_frequency'),
          tried: checkedValues('tried'),
        },
      };
    }

    function answerBrandLabel(value) {
      const labels = {
        garmin: 'Garmin',
        humminbird: 'Humminbird',
        'lowrance-simrad': 'Lowrance or Simrad',
        lowrance: 'Lowrance',
        airmar: 'Airmar',
        other: 'Another brand or not sure',
      };
      return labels[value] || 'Not confirmed';
    }

    function answerTransducerLabel(value, twoDOnly) {
      if (value && value !== 'UNKNOWN' && value !== '2D_ONLY') return customerOptionValue(value);
      return twoDOnly ? 'Not confirmed for this 2D-only route' : 'Not confirmed';
    }

    function answerData(selection) {
      const hasTwoD = selection.sonarModes.includes('2d');
      const hasConventionalImaging = selection.sonarModes.includes('down') || selection.sonarModes.includes('side');
      const hardwareAnswers = [];
      if (hasConventionalImaging) {
        hardwareAnswers.push({ label: '2D/down/side brand', value: answerBrandLabel(selection.conventionalBrand) });
        hardwareAnswers.push({ label: '2D/down/side transducer', value: answerTransducerLabel(selection.conventional, false) });
      } else if (hasTwoD) {
        hardwareAnswers.push({ label: '2D sonar brand', value: 'Not confirmed for this 2D-only route' });
        hardwareAnswers.push({ label: '2D transducer', value: answerTransducerLabel(selection.conventional, true) });
      }
      if (selection.hasFfs) {
        hardwareAnswers.push({ label: 'Forward-facing brand', value: answerBrandLabel(selection.ffsBrand) });
        hardwareAnswers.push({ label: 'Forward-facing transducer', value: answerTransducerLabel(selection.ffs, false) });
      }
      if (selection.mega360) hardwareAnswers.push({ label: '360 system', value: 'Humminbird 360/MEGA 360' });

      const sonarTypes = [
        ['2d', '2D sonar', selection.sonarModes.includes('2d')],
        ['down', 'Down Imaging', selection.sonarModes.includes('down')],
        ['side', 'Side Imaging', selection.sonarModes.includes('side')],
        ['ffs', 'Forward-Facing Sonar', selection.hasFfs],
        ['mega360', '360/MEGA 360', selection.mega360],
      ];
      return {
        hardwareAnswers,
        sonarTypes,
        selectedSonarTypes: sonarTypes.filter(function (item) { return item[2]; }).map(function (item) { return item[1]; }),
      };
    }

    function renderAnswerSummary(selection) {
      if (!answerHardware || !answerSonar) return;
      answerHardware.replaceChildren();
      answerSonar.replaceChildren();

      const answers = answerData(selection);
      answers.hardwareAnswers.forEach(function (row) {
        answerHardware.appendChild(create('dt', '', row.label));
        answerHardware.appendChild(create('dd', '', row.value));
      });

      answers.sonarTypes.forEach(function (item) {
        const selected = item[2];
        const chip = create('li', selected ? 'ffc-product-finder__answer-sonar--selected' : 'ffc-product-finder__answer-sonar--absent', selected ? item[1] : `No ${item[1]}`);
        answerSonar.appendChild(chip);
      });
    }

    async function showResults() {
      const selection = buildSelection();
      const recommendation = finder.buildRecommendation(selection);
      nextButton.disabled = true;
      status.textContent = 'Checking current products, prices and exact variants…';
      analyticsEvent('ffc_quiz_submitted', { recommendation_count: recommendation.recommendations.length });

      const settled = await Promise.allSettled(recommendation.recommendations.map(function (request, index) {
        return finder.resolveProduct(request, fetch, location.origin, `${Date.now()}-${index}`);
      }));
      const outcomes = settled.map(function (item, index) {
        return { item, index, request: recommendation.recommendations[index] };
      });
      const resolved = outcomes.filter(function (outcome) {
        return outcome.item.status === 'fulfilled';
      });
      const failures = outcomes.filter(function (outcome) {
        return outcome.item.status === 'rejected';
      });

      cards.replaceChildren();
      resolved.forEach(function (outcome) {
        const product = outcome.item.value;
        cards.appendChild(renderCard(
          product,
          outcome.index,
          outcome.index === 0 ? humminbirdCableConfirmation(selection) : ''
        ));
        analyticsEvent('ffc_quiz_recommendation_rendered', {
          product_handle: product.request.handle,
          variant_id: product.variantId,
          recommendation_position: outcome.index + 1,
        });
      });
      failures.forEach(function (outcome) {
        analyticsEvent('ffc_quiz_recommendation_failed', {
          product_handle: outcome.request.handle,
          recommendation_position: outcome.index + 1,
          failure_reason_class: lookupFailureClass(outcome.item.reason),
        });
      });

      renderAnswerSummary(selection);
      if (resultsEmail) {
        resultsEmail.hidden = resolved.length === 0 || !root.dataset.klaviyoCompanyId || !root.dataset.klaviyoListId;
        if (resultsEmailFeedback) resultsEmailFeedback.textContent = '';
        if (resultsEmailForm) {
          const submitButton = resultsEmailForm.querySelector('button[type="submit"]');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Email my results';
          }
          if (resultsEmailInput) resultsEmailInput.disabled = false;
        }
        resultsEmail.dataset.ffcSelection = JSON.stringify(selection);
      }
      if (resultsIntro) {
        if (resolved.length === 2) resultsIntro.textContent = 'We recommend these two products.';
        else if (resolved.length === 1) resultsIntro.textContent = 'Here is what we recommend.';
        else resultsIntro.textContent = 'We could not confirm a product right now.';
      }

      identification.hidden = !recommendation.needsIdentification;
      if (omitted) omitted.hidden = !recommendation.omittedNeeds.includes('mega360-guide');
      if (resolved.length === 0) {
        cards.appendChild(create('p', 'ffc-product-finder__no-match', 'We could not confirm an exact available product right now. Use the identification links below or contact Fishfinder Coach before buying.'));
        identification.hidden = false;
      } else if (failures.length) {
        cards.appendChild(create('p', 'ffc-product-finder__availability-note', 'One additional match was withheld because its exact current variant could not be confirmed.'));
      }

      wizard.hidden = true;
      root.classList.add('ffc-product-finder--results');
      results.hidden = false;
      status.textContent = '';
      nextButton.disabled = false;
      analyticsEvent('ffc_quiz_results_rendered', {
        recommendation_count: resolved.length,
        failure_count: failures.length,
      });
      results.focus({ preventScroll: true });
      results.scrollIntoView({ behavior: resultScrollBehavior(), block: 'start' });
    }

    form.addEventListener('change', function (event) {
      const target = event.target;
      if (target.name === 'sonar_modes') sanitizeSonarState();
      if (target.name === 'conventional_brand') {
        form.querySelectorAll('input[name="conventional"]').forEach(function (input) { input.checked = false; });
        syncBrandPanels('conventional');
        sanitizeSonarState();
      }
      if (target.name === 'conventional' || target.name === 'humminbird_unit') {
        if (target.name === 'conventional') clearAnswers('humminbird_unit');
        sanitizeSonarState();
      }
      if (target.name === 'ffs_brand') {
        form.querySelectorAll('input[name="ffs"]').forEach(function (input) { input.checked = false; });
        syncBrandPanels('ffs');
        sanitizeSonarState();
      }
      saveState();
    });

    nextButton.addEventListener('click', function () {
      const steps = activeSteps();
      const step = steps[currentIndex];
      if (!validateStep(step)) return;
      analyticsEvent('ffc_quiz_step_completed', {
        step_key: step.dataset.stepKey,
        step_number: currentIndex + 1,
        step_count: steps.length,
      });
      if (currentIndex < steps.length - 1) setStep(currentIndex + 1);
      else showResults();
    });

    backButton.addEventListener('click', function () {
      const step = activeSteps()[currentIndex];
      analyticsEvent('ffc_quiz_back', { from_step_key: step ? step.dataset.stepKey : '' });
      setStep(currentIndex - 1);
    });

    if (resultsEmailForm && resultsEmailInput && resultsEmailFeedback) {
      resultsEmailForm.addEventListener('submit', async function (event) {
        event.preventDefault();
        const email = resultsEmailInput.value.trim();
        if (!email || !resultsEmailInput.checkValidity()) {
          resultsEmailFeedback.textContent = 'Enter a valid email address.';
          resultsEmailInput.focus();
          return;
        }
        let selection;
        try { selection = JSON.parse(resultsEmail.dataset.ffcSelection || 'null'); } catch (_) { selection = null; }
        const recommendations = selection ? buildResultsEmailRecommendations(cards) : null;
        if (!selection || !recommendations) {
          resultsEmailFeedback.textContent = 'Your recommendation could not be prepared for email. Please try the quiz again.';
          return;
        }

        const submitButton = resultsEmailForm.querySelector('button[type="submit"]');
        submitButton.disabled = true;
        submitButton.textContent = 'Requesting…';
        resultsEmailInput.disabled = true;
        resultsEmailFeedback.textContent = '';
        try {
          const requestFingerprint = JSON.stringify({
            email: email.toLowerCase(),
            recommendations,
            selection,
          });
          if (!pendingResultsEmailRequest || pendingResultsEmailRequest.fingerprint !== requestFingerprint) {
            pendingResultsEmailRequest = { fingerprint: requestFingerprint, requestId: createRequestId() };
          }
          const requestId = pendingResultsEmailRequest.requestId;
          await submitResultsSignupOnce({
            companyId: root.dataset.klaviyoCompanyId,
            listId: root.dataset.klaviyoListId,
            email,
            requestId,
            quizVersion: VERSION,
            recommendations,
            selection,
          });
          pendingResultsEmailRequest = null;
          submitButton.textContent = 'Request received';
          resultsEmailFeedback.textContent = "Request received. We'll check in about two minutes to make sure this address can receive email marketing. If it can't, no email will be sent. Your results are still above.";
          analyticsEvent('ffc_quiz_results_email_requested', {
            product_handles: recommendations.map(function (recommendation) { return recommendation.handle; }),
            recommendation_count: recommendations.length,
          });
        } catch (_) {
          submitButton.disabled = false;
          submitButton.textContent = 'Email my results';
          resultsEmailInput.disabled = false;
          resultsEmailFeedback.textContent = 'We could not complete the email request right now. Your results are still above. Please try again.';
        }
      });
    }

    restartButton.addEventListener('click', function () {
      root.classList.remove('ffc-product-finder--results');
      results.hidden = true;
      wizard.hidden = false;
      cards.replaceChildren();
      currentIndex = 0;
      setStep(0);
    });

    restoreState();
    syncBrandPanels('conventional');
    syncBrandPanels('ffs');
    setStep(currentIndex, { focus: false });
    analyticsEvent('ffc_quiz_loaded');
  }

  function mount(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches('.ffc-product-finder')) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('.ffc-product-finder').forEach(init);
  }

  function bindLifecycle() {
    if (typeof document === 'undefined') return;
    const start = function () { mount(document); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();

    if (!globalThis.__ffcProductFinderSectionLoadBound) {
      document.addEventListener('shopify:section:load', function (event) {
        mount(event.target);
      });
      globalThis.__ffcProductFinderSectionLoadBound = true;
    }
  }

  bindLifecycle();
  return {
    HANDLE_TO_ROUTE,
    buildKlaviyoEventRequest,
    buildKlaviyoSubscriptionRequest,
    buildResultsEmailRecommendations,
    buildResultsEventProperties,
    createSignupSubmitter,
    init,
    isIntentionalClick,
    mount,
    submitResultsSignup,
  };
});
